Copyright 2022 Bio-Conversion Databank Foundation Licensed under the Apache
License, Version 2.0 (the "License"); you may not use this file except in
compliance with the License. You may obtain a copy of the License at
http://www.apache.org/licenses/LICENSE-2.0 Unless required by applicable law or
agreed to in writing, software distributed under the License is distributed on
an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express
or implied. See the License for the specific language governing permissions and
limitations under the License

<template>
  <v-stepper v-model="stepperCount" class="elevation-0">
    <v-stepper-header class="elevation-0 mb-n8 mt-n4">
      <v-stepper-step :complete="stepperCount > 1" step="1"> </v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step :complete="stepperCount > 2" step="2"> </v-stepper-step>

      <v-divider></v-divider>

      <v-stepper-step step="3"> </v-stepper-step>
    </v-stepper-header>
    <v-stepper-items>
      <v-stepper-content step="1">
        <p v-if="notLoggedIn" class="mb-10">
          <b>Set up Two-Factor Authentication (2FA)</b>
        </p>
        <div>
          <p>Please select from one of the following 2FA methods:</p>

          <v-col>
            <v-btn
              class="mr-4 mb-3"
              color="primary"
              @click="selectEmail"
              large
              elevation="6"
            >
              Email
            </v-btn>

            <v-btn
              class="mb-3"
              color="primary"
              @click="selectTOTP"
              large
              elevation="6"
            >
              Authenticator App
            </v-btn>
          </v-col>
        </div>

        <div v-if="notLoggedIn" class="mt-10">
          <v-btn
            text
            color="secondary"
            @click="onSkipAndLogin"
            large
            elevation="6"
            class="mr-2"
          >
            Skip and login
          </v-btn>

          <p style="font-size: 14px" class="mt-4 mb-8">
            You can set up 2FA later in your preferences.
          </p>

          <v-checkbox
            class="ml-16 mb-n4 mt-n2"
            dense
            v-model="promptMfa"
            value="true"
            false-value="true"
            label="Don't ask me again"
          ></v-checkbox>
        </div>
      </v-stepper-content>

      <v-stepper-content step="2">
        <p v-if="notLoggedIn"><b>Set up Two-Factor Authentication (2FA)</b></p>
        <v-form ref="formTwo" v-model="isFormTwoValid">
          <!-- TOTP related -->
          <div class="mt-4" v-if="selectedTOTP">
            <p>Please scan the QR code below using your authenticator app:</p>
            <QrcodeVue :value="qrcodeUri" />

            <p class="mt-4">Optionally, enter the setup key below:</p>
            <h3>{{ secretCode }}</h3>

            <div class="mt-8 mb-4">
              <v-btn
                text
                @click="backToStart"
                large
                elevation="6"
                class="mr-2"
                v-if="!makingAPICalls"
              >
                Back
              </v-btn>
              <v-btn
                color="primary"
                @click="stepperCount = 3"
                large
                elevation="6"
              >
                Next
              </v-btn>
            </div>
          </div>

          <!-- Email related -->
          <div class="mt-16" v-if="selectedEmail">
            <p>
              Please enter a different Email where you would like to receive 2FA
              passcodes:
            </p>
            <v-text-field
              class="mt-8"
              label="Email*"
              v-model="secondaryEmail"
              outlined
              clearable
              :rules="[validateEmail]"
            >
            </v-text-field>

            <div style="margin-top: 80px">
              <v-btn
                text
                @click="backToStart"
                large
                elevation="6"
                class="mx-2 mb-4"
                v-if="!makingAPICalls"
              >
                Back
              </v-btn>
              <v-btn
                color="primary"
                large
                :disabled="!isFormTwoValid"
                elevation="6"
                class="mb-4"
                v-if="!makingAPICalls"
                @click="
                  () => {
                    sendMfaEmailCode();
                  }
                "
              >
                Next
              </v-btn>

              <v-progress-circular
                v-if="makingAPICalls"
                indeterminate
                color="primary"
              ></v-progress-circular>
            </div>
          </div>
        </v-form>
      </v-stepper-content>
      <v-stepper-content step="3">
        <p v-if="notLoggedIn"><b>Set up Two-Factor Authentication (2FA)</b></p>
        <v-form
          ref="formThree"
          @submit.prevent="onVerificationCodeEntered"
          v-model="isFormThreeValid"
        >
          <!-- SMS and TOTP shared, with v-if guards for some components -->
          <div class="mt-16">
            <p v-if="selectedEmail">
              Please enter the verification code that was sent to your Email:
            </p>
            <p v-if="selectedTOTP">
              Please enter the authentication code from your authenticator app:
            </p>
            <p v-if="selectedEmail">Code Expires in: {{ timerMinutes }} m</p>
            <v-text-field
              class="mt-4"
              label="Code*"
              v-model="authCode"
              outlined
              clearable
              :rules="[validateIsNumber]"
            >
            </v-text-field>
            <v-btn
              v-if="selectedEmail"
              color="primary"
              @click="
                () => {
                  sendMfaEmailCode();
                }
              "
              large
              elevation="6"
              class="mt-3"
              :disabled="timerCount > 0"
            >
              Resend Code
            </v-btn>
            <div style="margin-top: 74px">
              <v-btn
                text
                @click="
                  {
                    stepperCount = 2;
                    $refs.formThree.reset();
                  }
                "
                large
                elevation="6"
                class="mx-2 mb-4"
              >
                Back
              </v-btn>
              <v-btn
                color="primary"
                type="submit"
                large
                :disabled="!isFormThreeValid"
                elevation="6"
                class="mb-4"
              >
                Verify
              </v-btn>
            </div>
          </div>
        </v-form>
      </v-stepper-content>
    </v-stepper-items>
  </v-stepper>
</template>

<script>
import UserPool from '@/plugins/UserPool';
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import {
  validatePhoneNumber,
  validateIsNumber,
  validateEmail,
} from '@/utilities/validationRules';
import QrcodeVue from 'qrcode.vue';
import { timeSaturday } from 'd3-time';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
const REGION = 'us-west-2';
let cognitoProviderName =
  'cognito-idp.' +
  REGION +
  '.amazonaws.com/' +
  process.env.VUE_APP_COGNITO_POOL_ID;
export default {
  name: 'Register2FA',
  props: {
    // used when a new user is being registered to authenticate the user; in any other case, this is null
    authDetails: AuthenticationDetails,
    // used when using the Register2FA component after logging in; this is null when new user is being registered
    authenticatedUser: CognitoUser,
    // used to reset the form back to the intial state
    triggerReset: Boolean,
  },
  components: {
    QrcodeVue,
  },
  data() {
    return {
      timer: null,
      timerEnabled: false,
      timerCount: 900,
      onEnterEmail: true,
      promptEmailMfa: false,
      secondaryEmail: '',
      emailMfaCode: '',
      email: '',
      // visual state
      stepperCount: 1,
      selectedEmail: false,
      //selectedSMS: false,
      selectedTOTP: false,
      makingAPICalls: false,
      notLoggedIn: true,
      // data
      user: {
        type: CognitoUser,
      },
      userEmail: '',
      phoneNumber: '',
      authCode: '',
      secretCode: '',
      promptMfa: 'true',
      // error handling
      isFormTwoValid: true,
      isFormThreeValid: true,
    };
  },
  computed: {
    timerMinutes() {
      return Math.round(this.timerCount / 60);
    },
    qrcodeUri() {
      // this is the format that the uri has to be in so that when it is scanned using an authenticator app,
      // the title of the code is automatically saved as "Bio-Conversion Databank Foundation (user@example.com)"
      return (
        'otpauth://totp/Bio-Conversion%20Databank%20Foundation%20(' +
        this.userEmail +
        ')?secret=' +
        this.secretCode
      );
    },
  },
  watch: {
    triggerReset(newVal) {
      if (newVal) {
        this.backToStart();
      }
    },
    //Incrementing timer for code expiry
    timerEnabled(value) {
      if (value) {
        setTimeout(() => {
          this.timerCount--;
        }, 1000);
      }
    },

    timerCount: {
      handler(value) {
        if (this.value <= 0) {
          this.timerEnabled = false;
        }
        if (value > 0 && this.timerEnabled) {
          setTimeout(() => {
            this.timerCount--;
          }, 1000);
        }
      },
      immediate: true, // This ensures the watcher is triggered upon creation
    },
  },
  created() {
    // if an authenticated user is passed in, then use that instead of authenticating a new user
    if (this.authenticatedUser) {
      this.user = this.authenticatedUser;
      this.notLoggedIn = false;
      this.fetchUserEmail();
    } else {
      this.user = new CognitoUser({
        Username: this.authDetails.getUsername(),
        Pool: UserPool,
      });

      // first, authenticate the user so we can save the phone number to their account
      this.user.authenticateUser(this.authDetails, {
        onSuccess: (data) => {
          console.log('user authenticated: ' + data);
          this.fetchUserEmail();
        },
        onFailure: (err) => {
          console.log('failed to authenticate user: ' + err);
        },
      });
    }
  },
  methods: {
    validatePhoneNumber,
    validateIsNumber,
    validateEmail,
    onSkipAndLogin() {
      // set the prompt-mfa attribute to user's choice
      this.updatePromptMfaAttribute(this.promptMfa);

      // mark user as authenticated and send them to the dashboard
      if (localStorage.getItem('rememberMe') == 'true') {
        localStorage.setItem('authenticated', 'true');
      } else {
        sessionStorage.setItem('authenticated', 'true');
      }
      this.$router.push({ name: 'Dashboard' });
    },
    backToStart() {
      // reset the 2FA selection state
      this.selectedEmail = false;
      //this.selectedSMS = false;
      this.selectedTOTP = false;

      // go back to first step
      this.stepperCount = 1;

      // reset the forms and the data in them
      this.$refs.formTwo.reset();
      this.$refs.formThree.reset();
    },
    selectEmail() {
      this.selectedEmail = true;
      this.stepperCount = 2;
    },
    selectTOTP() {
      this.selectedTOTP = true;
      this.stepperCount = 2;

      this.user.associateSoftwareToken({
        associateSecretCode: (secretCode) => {
          console.log('retrieved secret code: ' + secretCode);

          this.secretCode = secretCode;
        },
        onFailure: (err) => {
          console.log(err);
        },
      });
    },
    enteredPhoneNumber() {
      // show loading icon while making API calls
      this.makingAPICalls = true;

      // first, update the phone_number attribute to the entered phone number
      const userEmailAttribute = {
        Name: 'userEmail',
        Value: this.userEmail,
      };
      this.user.updateAttributes([userEmailAttribute], (err, data) => {
        if (err) {
          console.log('failed to update Email: ' + err);

          // stop showing loading icon when API calls end
          this.makingAPICalls = false;
        } else {
          console.log("updated user's email: " + data);

          // send a verification code to the phone number
          this.user.getAttributeVerificationCode('userEmail', {
            onSuccess: (data) => {
              console.log('Email verification code sent: ' + data);

              // stop showing loading icon when API calls end
              this.makingAPICalls = false;
            },
            onFailure: (err) => {
              console.log(err);

              // stop showing loading icon when API calls end
              this.makingAPICalls = false;
            },
            inputVerificationCode: (data) => {
              console.log(data);

              // stop showing loading icon when API calls end
              this.makingAPICalls = false;

              // go to next step to enter the verification code
              this.stepperCount = 3;
            },
          });
        }
      });
    },
    async sendMfaEmailCode() {
      this.timerEnabled = true;
      this.timerCount = 900;
      //Initializing Lambda Client
      const lambda = new LambdaClient({
        region: REGION,
        credentials: fromCognitoIdentityPool({
          client: new CognitoIdentityClient({ region: REGION }),
          identityPoolId: process.env.VUE_APP_COGNITO_IDENTITY_POOL_ID_LAMBDA,
          logins: {
            [`${cognitoProviderName}`]:
              this.user.signInUserSession.idToken.jwtToken,
          },
        }),
      });
      //setting random code to send to user
      this.stepperCount = 3;
      const min = 10000;
      const max = 99999;
      this.emailMfaCode = Math.floor(Math.random() * (max - min)) + min;
      const params = {
        FunctionName: 'email2FA',
        InvocationType: 'RequestResponse',
        LogType: 'None',
        Payload: JSON.stringify({
          email: this.secondaryEmail,
          code: this.emailMfaCode,
        }),
      };
      const user = await UserPool.getCurrentUser();
      try {
        await lambda.send(new InvokeCommand(params));
        this.stepperCount = 3;
      } catch (error) {
        console.log(error);
      }
    },
    onSendCode() {
      this.user = new CognitoUser({
        Username: this.email,
        Pool: UserPool,
      });
      this.user.resendConfirmationCode((err, data) => {
        if (err) {
          console.log(err); // DEBUG
          const notification = {
            type: 'error',
            message: err.message,
          };
          this.$store.dispatch('addNotification', notification);
        } else {
          console.log(data); // DEBUG
          const notification = {
            type: 'success',
            message: 'Confirmation code is sent',
          };
          this.$store.dispatch('addNotification', notification);
        }
        this.stepperCount = 3;
      });
      this.stepperCount = 3;
    },
    async onVerificationCodeEntered() {
      if (this.selectedEmail) {
        if (this.timerCount <= 0) {
          const notification = {
            type: 'error',
            message: 'Code has expired',
          };
          this.$store.dispatch('addNotification', notification);
          return;
        }
        const user = await UserPool.getCurrentUser();
        user.getSession((err, session) => {
          if (this.authCode == this.emailMfaCode) {
            let attributeList = [
              new CognitoUserAttribute({
                Name: 'custom:prompt-email-mfa',
                Value: 'true',
              }),
              new CognitoUserAttribute({
                Name: 'custom:alternate-email-2fa',
                Value: this.secondaryEmail,
              }),
            ];
            user.updateAttributes(attributeList, (session, err) => {
              this.updatePromptMfaAttribute('false');
              const notification = {
                type: 'success',
                message: 'Successfully set up 2FA',
              };
              this.$store.dispatch('addNotification', notification);
            });
            this.finishSetup();
          } else {
            const notification = {
              type: 'error',
              message: 'Check your authentication code again',
            };
            this.$store.dispatch('addNotification', notification);
          }
        });
      }
      if (this.selectedTOTP) {
        // verify the TOTP from the authenticator app
        this.user.verifySoftwareToken(this.authCode, 'MFA Device', {
          onSuccess: (session) => {
            console.log('TOTP code verified: ' + session);

            // once TOTP is verified, set the user's MFA preference
            const softwareTokenMfaParams = {
              Enabled: true,
              PreferredMfa: true,
            };
            this.user.setUserMfaPreference(
              null,
              softwareTokenMfaParams,
              (err, data) => {
                if (err) {
                  console.log('failed to set user MFA preference: ' + err);
                } else {
                  console.log('set user MFA preference: ' + data);

                  // set the prompt-mfa attribute to false since it has now been setup
                  this.updatePromptMfaAttribute('false');

                  const notification = {
                    type: 'success',
                    message: 'Successfully set up 2FA',
                  };
                  this.$store.dispatch('addNotification', notification);

                  this.finishSetup();
                }
              },
            );
          },
          onFailure: (err) => {
            console.log('error when verifying TOTP code: ' + err);

            const notification = {
              type: 'error',
              message: err.message,
            };
            this.$store.dispatch('addNotification', notification);
          },
        });
      }
    },
    //SMS alternative verification method
    fetchUserEmail() {
      this.user.getUserAttributes((err, attributes) => {
        if (err) {
          console.log(err);
        } else {
          // find the email attribute
          const emailAttribute = attributes.find((attribute) => {
            return attribute.Name == 'email';
          });

          // set the email to the global variable
          this.userEmail = emailAttribute.Value;
          console.log(this.userEmail);
        }
      });
    },
    updatePromptMfaAttribute(promptMfa) {
      let attributePromptMfa = new CognitoUserAttribute({
        Name: 'custom:prompt-mfa',
        Value: promptMfa,
      });

      this.user.updateAttributes([attributePromptMfa], (err, result) => {
        if (err) {
          console.log(err);
        } else {
          console.log(result);
        }
      });
    },
    finishSetup() {
      // either emit the setup-success signal or log the user in once setup is complete
      if (this.authenticatedUser) {
        this.$emit('setup-success');
        // reset the UI
        this.backToStart();
      } else {
        // mark user as authenticated and send them to the dashboard once verified
        if (localStorage.getItem('rememberMe') == 'true') {
          localStorage.setItem('authenticated', 'true');
        } else {
          sessionStorage.setItem('authenticated', 'true');
        }
        this.$router.push({ name: 'Dashboard' });
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
};
</script>
